import { Header, HeaderLogo, HeaderNav, HeaderNavLink, HeaderActions, Footer, Button, GlassCard } from '@lukhas/ui'
import { Users, BookOpen, Shield, Code, GitBranch, Heart, MessageSquare, Github } from 'lucide-react'

export default function CommunityPage() {
  return (
    <div className="min-h-screen bg-consciousness-deep">
      {/* Header */}
      <Header className="fixed top-0 left-0 right-0 z-50 bg-consciousness-deep/80 backdrop-blur-md border-b border-code-cyan/20">
        <HeaderLogo href="/">
          <span className="text-2xl font-light tracking-[0.15em] text-awareness-silver">
            LUKHAS<span className="text-code-cyan">.DEV</span>
          </span>
        </HeaderLogo>
        <HeaderNav>
          <HeaderNavLink href="/docs">Docs</HeaderNavLink>
          <HeaderNavLink href="/community">Community</HeaderNavLink>
          <HeaderNavLink href="/playground">Playground</HeaderNavLink>
          <HeaderNavLink href="https://lukhas.com">Enterprise</HeaderNavLink>
        </HeaderNav>
        <HeaderActions>
          <a href="https://lukhas.id/login" target="_blank" rel="noopener noreferrer">
            <Button variant="ghost">Sign In</Button>
          </a>
          <Button className="bg-code-gradient text-white">
            Get API Key
          </Button>
        </HeaderActions>
      </Header>

      {/* Hero */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6 flex justify-center">
            <Users className="w-16 h-16 text-code-cyan animate-pulse" strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-[0.15em] mb-6 text-awareness-silver">
            <span className="text-transparent bg-clip-text bg-code-gradient">Community</span> & Governance
          </h1>
          <p className="text-xl text-awareness-silver/80 max-w-4xl mx-auto">
            Building consciousness technology together through collaboration, research partnerships, and open dialogue
          </p>
        </div>
      </section>

      {/* Repository Strategy */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-light tracking-wide mb-4 text-awareness-silver">
              Repository <span className="text-code-cyan">Strategy</span>
            </h2>
            <p className="text-lg text-awareness-silver/80">
              Selective disclosure model balancing transparency with competitive advantage
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <GlassCard className="border-green-400/20">
              <div className="p-8">
                <div className="mb-4">
                  <Code className="w-10 h-10 text-green-400" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-light tracking-wide mb-4 text-awareness-silver">
                  What's <span className="text-green-400">Public</span>
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Open-source infrastructure and development resources
                </p>
                <ul className="space-y-2 text-sm text-awareness-silver/70">
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span>Infrastructure & Tooling (development tools, scripts, utilities)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span>APIs & Interfaces (public API specs, OpenAPI schemas)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span>Basic Authentication (WebAuthn/passkey foundation)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span>Architecture Documentation (high-level system design)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-400 mt-1">✓</span>
                    <span>Integration Examples (SDK usage patterns)</span>
                  </li>
                </ul>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <Shield className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-2xl font-light tracking-wide mb-4 text-awareness-silver">
                  What's <span className="text-code-cyan">Proprietary</span>
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Protected research available to collaborators by request
                </p>
                <ul className="space-y-2 text-sm text-awareness-silver/70">
                  <li className="flex items-start gap-2">
                    <span className="text-code-cyan mt-1">🔒</span>
                    <span>Core AI Research (MATRIZ engine, consciousness orchestration)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-code-cyan mt-1">🔒</span>
                    <span>Symbolic DNA (cognitive node implementations)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-code-cyan mt-1">🔒</span>
                    <span>Advanced Memory Systems (fold-based, emotional, procedural)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-code-cyan mt-1">🔒</span>
                    <span>Bio/Quantum-Inspired Algorithms</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-code-cyan mt-1">🔒</span>
                    <span>Strategic Documentation (roadmaps, partnerships)</span>
                  </li>
                </ul>
              </div>
            </GlassCard>
          </div>

          <GlassCard className="border-code-cyan/20 bg-code-gradient/5">
            <div className="p-8">
              <h3 className="text-xl font-light tracking-wide mb-4 text-awareness-silver">
                Protection Summary
              </h3>
              <p className="text-sm text-awareness-silver/80 mb-4">
                As of November 25, 2025, we've protected <span className="text-code-cyan font-semibold">125 files (41,047 lines)</span> of strategic documentation and proprietary AI research from public tracking while keeping them available locally for authorized collaborators.
              </p>
              <div className="flex gap-4 text-xs text-awareness-silver/60">
                <span>73 strategic business files (24,677 lines)</span>
                <span>•</span>
                <span>52 proprietary AI modules (16,370 lines)</span>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* Collaboration Opportunities */}
      <section className="py-16 px-6 bg-consciousness-deep/80">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-light tracking-wide mb-4 text-awareness-silver">
              Collaboration <span className="text-code-cyan">Opportunities</span>
            </h2>
            <p className="text-lg text-awareness-silver/80">
              Request access to proprietary components for research and enterprise use
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <BookOpen className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-light tracking-wide mb-3 text-awareness-silver">
                  Academic Research
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-6">
                  Research partnerships with universities and institutions under NDA
                </p>
                <div className="space-y-3 text-xs text-awareness-silver/70 mb-6">
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Access to proprietary modules</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Research publication rights</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>5 business day response</span>
                  </div>
                </div>
                <Button variant="ghost" className="text-code-cyan text-sm w-full">
                  Contact licensing@lukhas.ai
                </Button>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <Shield className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-light tracking-wide mb-3 text-awareness-silver">
                  Enterprise Partners
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-6">
                  Commercial licensing with full source access and enterprise support
                </p>
                <div className="space-y-3 text-xs text-awareness-silver/70 mb-6">
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Production use licensing</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Full source code access</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Dedicated support plans</span>
                  </div>
                </div>
                <Button variant="ghost" className="text-code-cyan text-sm w-full">
                  Contact licensing@lukhas.ai
                </Button>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <Code className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-light tracking-wide mb-3 text-awareness-silver">
                  Open-Source Contributors
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-6">
                  Contribute to public infrastructure with signed CLA
                </p>
                <div className="space-y-3 text-xs text-awareness-silver/70 mb-6">
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Documentation improvements</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Bug reports and fixes</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-code-cyan">→</span>
                    <span>Public infrastructure code</span>
                  </div>
                </div>
                <Button variant="ghost" className="text-code-cyan text-sm w-full">
                  View CONTRIBUTING.md
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Governance Documentation */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-light tracking-wide mb-4 text-awareness-silver">
              Governance <span className="text-code-cyan">Framework</span>
            </h2>
            <p className="text-lg text-awareness-silver/80">
              Professional standards for community collaboration
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <GitBranch className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    Contributing Guide
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Complete guide for contributing to public infrastructure, code quality standards, and submission process
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas/blob/main/CONTRIBUTING.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View CONTRIBUTING.md →
                </a>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Heart className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    Code of Conduct
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Professional standards, expected behavior, enforcement process, and community guidelines
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas/blob/main/CODE_OF_CONDUCT.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View CODE_OF_CONDUCT.md →
                </a>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Shield className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    Security Policy
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Vulnerability reporting process, response timeline, severity levels, and safe harbor provisions
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas/blob/main/SECURITY.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View SECURITY.md →
                </a>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <BookOpen className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    .gitignore Strategy
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Complete documentation of protection strategy, verification commands, and collaboration model
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas/blob/main/docs/GITIGNORE_STRATEGY.md"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View GITIGNORE_STRATEGY.md →
                </a>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Code className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    License
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  Proprietary license with viewing-only terms, commercial licensing requirements, and collaboration provisions
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas/blob/main/LICENSE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View LICENSE →
                </a>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-6">
                <div className="mb-4 flex items-center gap-3">
                  <Github className="w-8 h-8 text-code-cyan" strokeWidth={1.5} />
                  <h3 className="text-lg font-light tracking-wide text-awareness-silver">
                    GitHub Repository
                  </h3>
                </div>
                <p className="text-sm text-awareness-silver/80 mb-4">
                  View the public repository, open issues, contribute to discussions, and explore the codebase
                </p>
                <a
                  href="https://github.com/LukhasAI/Lukhas"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-code-cyan text-sm hover:underline"
                >
                  View Repository →
                </a>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Community Channels */}
      <section className="py-16 px-6 bg-consciousness-deep/80">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <h2 className="text-4xl font-light tracking-wide mb-4 text-awareness-silver">
              Community <span className="text-code-cyan">Channels</span>
            </h2>
            <p className="text-lg text-awareness-silver/80">
              Connect with developers, researchers, and the LUKHAS team
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <MessageSquare className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-light tracking-wide mb-3 text-awareness-silver">
                  GitHub Discussions
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-6">
                  Ask questions, share ideas, and discuss consciousness technology development
                </p>
                <Button variant="ghost" className="text-code-cyan text-sm w-full">
                  Join Discussions →
                </Button>
              </div>
            </GlassCard>

            <GlassCard className="border-code-cyan/20">
              <div className="p-8">
                <div className="mb-4">
                  <Users className="w-10 h-10 text-code-cyan" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-light tracking-wide mb-3 text-awareness-silver">
                  Discord Community
                </h3>
                <p className="text-sm text-awareness-silver/80 mb-6">
                  Real-time chat with developers, get help, and share your projects
                </p>
                <Button variant="ghost" className="text-code-cyan text-sm w-full">
                  Join Discord →
                </Button>
              </div>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 px-6 bg-code-gradient">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light tracking-[0.1em] mb-6 text-white">
            Request Collaboration Access
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us to discuss research partnerships, enterprise licensing, or collaboration opportunities
          </p>
          <div className="space-y-4 mb-12">
            <div className="flex items-center justify-center gap-2 text-white/80">
              <span className="font-semibold">Licensing & Partnerships:</span>
              <a href="mailto:licensing@lukhas.ai" className="text-white underline hover:text-white/90">
                licensing@lukhas.ai
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 text-white/80">
              <span className="font-semibold">Response Time:</span>
              <span>5 business days</span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-6">
            <a href="mailto:licensing@lukhas.ai">
              <Button size="lg" className="bg-white text-code-cyan px-12 py-6 text-lg hover:bg-awareness-silver">
                Contact for Access
              </Button>
            </a>
            <a href="https://github.com/LukhasAI/Lukhas">
              <Button size="lg" variant="ghost" className="bg-white/10 text-white px-12 py-6 text-lg hover:bg-white/20">
                View Public Repository
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer activeDomain="lukhas.dev">
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Developer Resources</h3>
          <ul className="space-y-2">
            <li><a href="/docs" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Documentation</a></li>
            <li><a href="/docs/api" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">API Reference</a></li>
            <li><a href="/playground" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Playground</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Community</h3>
          <ul className="space-y-2">
            <li><a href="/community" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Governance</a></li>
            <li><a href="https://github.com/LukhasAI/Lukhas/blob/main/CONTRIBUTING.md" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Contributing</a></li>
            <li><a href="https://github.com/LukhasAI/Lukhas/blob/main/CODE_OF_CONDUCT.md" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Code of Conduct</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Legal</h3>
          <ul className="space-y-2">
            <li><a href="https://github.com/LukhasAI/Lukhas/blob/main/LICENSE" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">License</a></li>
            <li><a href="https://github.com/LukhasAI/Lukhas/blob/main/SECURITY.md" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Security Policy</a></li>
            <li><a href="/privacy" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Privacy</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-awareness-silver mb-3">Other Platforms</h3>
          <ul className="space-y-2">
            <li><a href="https://lukhas.ai" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Main Platform</a></li>
            <li><a href="https://lukhas.com" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Enterprise</a></li>
            <li><a href="https://lukhas.io" className="text-sm text-awareness-silver/70 hover:text-code-cyan transition-colors">Infrastructure</a></li>
          </ul>
        </div>
      </Footer>
    </div>
  )
}
